import React from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectModalId } from "../../features/modal/state/modalSelectors";
import { useServicePlanPageContext } from "../ServicePlanPageContext";
import { useUpdateServicePlanGoalForm } from "../hooks/useUpdateServicePlanGoalForm";
import { closeModal } from "../../features/modal/state/modalSlice";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import ErrorText from "../../app/components/ErrorText";
import ControlledInput from "../../app/components/inputs/ControlledInput";
import ControlledSelect from "../../app/components/inputs/ControlledSelect";
import SpinnerButton from "../../app/components/buttons/SpinnerButton";

interface Props {
  goalId: string;
}

export const updateServicePlanGoalFormDialogId = "update-service-plan-dialog";

export const UpdateServicePlanGoalFormDialog: React.FC<Props> = ({
  goalId,
}) => {
  const modalId = useAppSelector(selectModalId);
  const dispatch = useAppDispatch();

  const {
    servicePlan: { goals },
  } = useServicePlanPageContext();

  const goal = goals.find((g) => g.id === goalId);

  const {
    state: {
      updateGoalRequestState: { isLoading, error },
      goalStatusOptions,
      domainNames,
    },
    form: { isValid, clearForm, control },
    handlers: { handleSubmit },
  } = useUpdateServicePlanGoalForm(goal!);

  const handleCloseModal = () => {
    clearForm();
    dispatch(closeModal());
  };

  return (
    <Dialog
      open={modalId === updateServicePlanGoalFormDialogId}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Update a Service Plan Goal
          <ErrorText error={error?.message} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ControlledInput
                name="name"
                label="Name"
                type="text"
                control={control}
                disabled={isLoading}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledSelect
                control={control}
                name={"status"}
                options={goalStatusOptions}
                fullWidth
                disabled={isLoading}
                label={"Goal Status"}
              />
            </Grid>

            {domainNames.length === 0 && (
              <Grid item md={12} xs={12}>
                <Typography
                  color="textSecondary"
                  style={{ fontSize: "0.8rem" }}
                >
                  *Cannot associate NIRVANA domains without first submitting a
                  NIRVANA Assessment for this case.
                </Typography>
              </Grid>
            )}
            <Grid item md={12} xs={12}>
              <ControlledSelect
                name="domains"
                label="Associated Domain(s)"
                control={control}
                disabled={domainNames.length === 0 || isLoading}
                fullWidth
                multiple
                options={[
                  ...domainNames.map((name) => ({ value: name, label: name })),
                ]}
              />
            </Grid>
            <Grid item md={12} xs={12}>
              <ControlledInput
                name="notes"
                label="Notes"
                control={control}
                disabled={isLoading}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SpinnerButton
            content="Cancel"
            showSpinner={false}
            type="button"
            onClick={handleCloseModal}
            disabled={isLoading}
          />
          <SpinnerButton
            showSpinner={isLoading}
            content="Save"
            color="primary"
            type="submit"
            disabled={!isValid || isLoading}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};
